const STATIC_CONSTRAINTS = Object.freeze([
    ['aum_musd', 'aum', 1_000_000],
    ['gross_market_value_musd', 'gmv', 1_000_000],
    ['net_market_value_abs_pct_gmv', 'nmv_pct_gmv', 1],
    ['position_abs_pct_aum', 'position_gmv_pct_of_aum', 1],
    ['beta_exposure_abs_pct_aum', 'beta_exp_as_pct_of_aum', 1],
    ['factor_vol_pct_aum', 'factor_risk_pct_of_total', 1],
    ['portfolio_vol_pct_aum', 'port_vol_pct_aum', 1],
    ['max_trade_abs_pct_existing_position', 'ticker_gmv_pct_delta', 1],
]);

// Largest trade counts a small or zero position as at least this share of AUM
// (MAX_TRADE_POSITION_FLOOR_PCT_AUM in the backend's optimizer_trading_rules).
export const LARGEST_TRADE_FLOOR_PCT_AUM = 0.5;

function finiteNonnegative(value, label) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
        throw new Error(`${label} must have a finite nonnegative value.`);
    }
    return value;
}

function requiredText(value, label) {
    if (typeof value !== 'string' || !value.trim() || value !== value.trim()) {
        throw new Error(`${label} must have an exact identity.`);
    }
    return value;
}

function compareCanonicalText(left, right) {
    return left < right ? -1 : left > right ? 1 : 0;
}

export function optimizerDynamicConstraintKey(factorName, riskType = 'Factor') {
    return JSON.stringify([
        requiredText(factorName, 'Factor constraint'),
        requiredText(riskType, 'Factor risk type'),
    ]);
}

// Attach a factor limit. Re-attaching keeps an existing record's limit and only
// switches it back on; a new record starts from the default limit.
export function withAttachedFactorConstraint(
    constraints,
    factorName,
    riskType = 'Factor',
    defaultValue = 10.0,
) {
    const key = optimizerDynamicConstraintKey(factorName, riskType);
    const existing = constraints?.dynamic_constraints?.[key];
    return {
        ...constraints,
        dynamic_constraints: {
            ...constraints?.dynamic_constraints,
            [key]: existing
                ? { ...existing, enabled: true }
                : {
                    factor_name: factorName,
                    risk_type: riskType,
                    enabled: true,
                    value: defaultValue,
                },
        },
    };
}

export function buildOptimizerConstraintDocument({ constraints, constraintToggles }) {
    const values = constraints?.static_constraints;
    const toggles = constraintToggles?.static_constraints;
    if (!values || typeof values !== 'object' || !toggles || typeof toggles !== 'object') {
        throw new Error('Optimizer constraint editor state is incomplete.');
    }

    const staticRecords = STATIC_CONSTRAINTS.map(([name, field, divisor]) => ({
        name,
        enabled: field === 'aum' ? true : toggles[field] === true,
        value: finiteNonnegative(values[field], field) / divisor,
    }));

    const dynamic = constraints?.dynamic_constraints ?? {};
    if (!dynamic || typeof dynamic !== 'object' || Array.isArray(dynamic)) {
        throw new Error('Optimizer factor constraint state is invalid.');
    }
    const factorRecords = Object.values(dynamic).map((record) => {
        if (!record || typeof record !== 'object' || Array.isArray(record)) {
            throw new Error('Optimizer factor constraint state is not explicitly identified.');
        }
        if (record.risk_type !== 'Factor') {
            throw new Error('Optimizer factor constraint risk type is unsupported.');
        }
        return {
            name: 'factor_vol_bps_gmv',
            factor: requiredText(record.factor_name, 'Factor constraint'),
            enabled: record.enabled === true,
            value: finiteNonnegative(record.value, `Factor constraint ${record.factor_name}`),
        };
    }).sort((left, right) => compareCanonicalText(left.factor, right.factor));
    if (new Set(factorRecords.map(({ factor }) => factor)).size !== factorRecords.length) {
        throw new Error('Optimizer factor constraints contain duplicate identities.');
    }
    return {
        static: staticRecords,
        factor: factorRecords,
    };
}

export function decodeOptimizerConstraintDocument(document) {
    if (
        !document || typeof document !== 'object' || Array.isArray(document)
        || !Array.isArray(document.static)
        || !Array.isArray(document.factor)
    ) {
        throw new Error('Saved optimizer constraint document is invalid.');
    }
    const constraints = { static_constraints: {}, dynamic_constraints: {} };
    const constraintToggles = { static_constraints: {} };
    if (document.static.length !== STATIC_CONSTRAINTS.length) {
        throw new Error('Saved optimizer static constraints are incomplete.');
    }
    STATIC_CONSTRAINTS.forEach(([name, field, multiplier], index) => {
        const record = document.static[index];
        if (record?.name !== name || typeof record?.enabled !== 'boolean') {
            throw new Error('Saved optimizer static constraint order is invalid.');
        }
        constraints.static_constraints[field] = finiteNonnegative(record.value, name) * multiplier;
        constraintToggles.static_constraints[field] = field === 'aum' ? true : record.enabled;
    });
    let priorFactor = null;
    document.factor.forEach((record) => {
        const factorName = requiredText(record?.factor, 'Saved factor constraint');
        if (
            record?.name !== 'factor_vol_bps_gmv'
            || typeof record?.enabled !== 'boolean'
            || (priorFactor && factorName <= priorFactor)
        ) {
            throw new Error('Saved optimizer factor constraints are invalid.');
        }
        priorFactor = factorName;
        const key = optimizerDynamicConstraintKey(factorName, 'Factor');
        constraints.dynamic_constraints[key] = {
            factor_name: factorName,
            risk_type: 'Factor',
            enabled: record.enabled,
            value: finiteNonnegative(record.value, factorName),
        };
    });
    return { constraints, constraintToggles };
}

export function canonicalJsonObject(value) {
    if (Array.isArray(value)) return value.map(canonicalJsonObject);
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.keys(value).sort().map((key) => [key, canonicalJsonObject(value[key])]),
        );
    }
    return value;
}

export function createOptimizerOperationId() {
    if (typeof window === 'undefined' || typeof window.crypto?.randomUUID !== 'function') {
        throw new Error('Secure optimizer operation identifiers are unavailable.');
    }
    return window.crypto.randomUUID();
}
